import { Tab, Tabs, Badge, IconButton } from '@material-ui/core';
import { productImg } from 'app/core/api/product-assignment/api-product-assignment.types';
import { useGetChatList } from 'app/core/api/repairer/api-repairer';
import {
  CaseStatus,
  RepairCase,
} from 'app/core/api/repairer/api-repairer.types';
import { getOtherUsersName } from 'app/core/api/user/user-utils';
import { UserRole } from 'app/core/api/user/api-user.types';
import styledConsts from 'app/styles/styledConsts';
import styledFuncs from 'app/styles/styledFuncs';
import styledMixins from 'app/styles/styledMixins';
import { ThemeProps } from 'app/styles/theme';
import {
  useEffectOnce,
  usePollInterval,
  useQueryParams,
  useUrlParams,
  useWatchAsRef,
} from 'app/tools/hooks/misc-hooks';
import { sentenceCase } from 'app/tools/utils';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import styled, { css } from 'styled-components/macro';
import { UrlQueryKey } from 'app/core/api/api-core/api-constants';
import {
  useConsumerReadNotification,
  useRepairerReadNotification,
} from 'app/core/api/company/api-company';
import { appEnv, AppVariant } from 'app/core/environment';
import Form from 'app/shared/frames/Form';
import TextInput from 'app/shared/components/form-input/TextInput';
import Icon from 'app/shared/components/icon/Icon';
import IconLib from 'app/shared/components/icon/icon-lib';
import { DefectTypesMap } from 'app/consumer-app/views/report-fault/report-fault-utils';
import ProductPictureOrDefault from '../../components/app-image/ProductPictureOrDefault';
import Button from '../../components/button/Button';
import { ChatItemProps } from '../../components/chat/MsgProps';
import Body from '../../components/typography/body/Body';
import LoadingOverlay from '../../page-parts/LoadingOverlay';
import SharedRoutePath from '../../SharedRoutePath';
import { EMPTY_MESSAGE } from './chat-utils';

const StyledLabel = styled.label`
  font-weight: 500;
`;

export type ChatFilterKey = 'completed' | 'initiated' | 'inProgress';
type ChatFilter = (c: RepairCase) => boolean;
const chatFilters: { key: ChatFilterKey; filter: ChatFilter }[] = [
  // { key: 'all', filter: () => true },
  {
    key: 'initiated',
    filter: c => c.chatListStatus === CaseStatus.NEW,
  },
  {
    key: 'inProgress',
    filter: c => c.chatListStatus === CaseStatus.IN_PROGRESS,
  },
  {
    key: 'completed',
    // filter: c => c.chatListStatus === CaseStatus.COMPLETE,
    filter: c => c.chatListStatus === 'complete',
  },
];
const StyledChatItemBox = styled(Button).attrs({
  plain: true,
})`
  margin: ${styledFuncs.pad(1)};
  padding: ${styledFuncs.pad(1)};
  ${styledMixins.roundCorners}
  ${styledMixins.softShadowForButtons}
  ${styledMixins.collapseFix}
  .MuiButton-label {
    display: flex;
    align-items: center;
  }
`;

const UserChatDetail = styled.div`
  padding: 0 ${styledFuncs.pad(1)};
  width: calc(100% - 145px);
  line-height: 1.4;
  label {
    display: block;
    font-weight: 600;
    font-size: 15px;
    margin-bottom: ${styledFuncs.pad(0.5)};
    text-align: left;
    ${styledMixins.lineClamp(2)}
  }
  span {
    font-size: 12px;
    color: #b4b4b4;
    display: block;
    text-align: left;
    ${styledMixins.lineClamp(2)}
  }
`;

const mapStatusToColor = (
  status: CaseStatus
): ((p: ThemeProps) => string) | string =>
  ({
    [CaseStatus.NEW]: styledConsts.cWarning,
    [CaseStatus.IN_PROGRESS]: styledConsts.cWarning,
    [CaseStatus.RESOLVED]: styledConsts.cPrimary,
    [CaseStatus.COMPLETE]: styledConsts.cPrimary,
    [CaseStatus.ABANDONED]: '#929292',
    [CaseStatus.FAILED]: styledConsts.cError,
  }[status]);

const ChatTimeStatus = styled.div<{ $status: CaseStatus }>`
  text-align: end;
  margin-left: auto;
  margin-bottom: 30px;
  margin-right: ${styledFuncs.pad(0.7)};
  width: 85px;
  line-height: 1.4;
  label {
    display: block;
    color: ${p => mapStatusToColor(p.$status)};
    margin-bottom: ${styledFuncs.pad(1.5)};
    font-size: 13px;
  }
  span {
    font-weight: 600;
    font-size: 13px;
  }
`;

const CenteredBox = styled.div`
  flex-grow: 1;
  flex-direction: column;
  display: flex;
  justify-content: center;
  text-align: center;
  > * {
    margin: ${styledFuncs.pad(0.5)} 0;
  }
`;

const NotificationCount = styled.div`
  position: absolute;
  width: ${styledFuncs.pad(2)};
  height: ${styledFuncs.pad(2)};
  right: ${styledFuncs.pad(2.9)};
  bottom: ${styledFuncs.pad(1)};
  border-radius: 10px;
  background: #31ad31;
  text-align: center;
  color: white;
  z-index: 1;
  padding: 1px;
`;
function ChatListItem({ repairCase }: ChatItemProps): JSX.Element {
  const history = useHistory();
  const { request$: readConsumerNotification$ } =
    useConsumerReadNotification();
  const { request$: readRepairerNotification$ } =
    useRepairerReadNotification();
  const isConsumer = appEnv.APP_VARIANT === AppVariant.CONSUMER;
  const onItemSelect = () => {
    if (isConsumer) {
      readConsumerNotification$(repairCase.id).subscribe();
    } else {
      readRepairerNotification$(repairCase.id).subscribe();
    }
    history.push(`${SharedRoutePath.CHAT}/${repairCase.id}`);
  };
  const { lastEvent, status, productDefectDetails } = repairCase;
  const { assignedProductDetails } = productDefectDetails;
  const { productDetails } = assignedProductDetails;
  return (
    <StyledChatItemBox onClick={onItemSelect}>
      <ProductPictureOrDefault
        css={css`
          padding: ${styledFuncs.pad(0.5)};
          > * {
            height: 3.5rem;
            width: 3.5rem;
          }
        `}
        height="3rem"
        productId={productDetails.id}
        src={productImg(assignedProductDetails)}
        alt="product image"
      />
      <UserChatDetail>
        <StyledLabel>
          {getOtherUsersName(repairCase, 'full-name') || 'Chat'}
        </StyledLabel>
        {isConsumer && (
          <span>
            {lastEvent?.displayText === EMPTY_MESSAGE
              ? sentenceCase(lastEvent.eventType)
              : lastEvent?.displayText}
          </span>
        )}
        {!isConsumer && (
          <span
            style={{
              color: '#000000',
              fontSize: '10px',
              fontWeight: 500,
              marginBottom: '5px',
            }}
          >
            {productDetails.name}
          </span>
        )}
        {!isConsumer && (
          <span
            style={{
              color: '#000000',
              fontSize: '10px',
              fontWeight: 400,
            }}
          >
            {DefectTypesMap.get(productDefectDetails.title)}
          </span>
        )}
      </UserChatDetail>
      <ChatTimeStatus $status={status}>
        {/* <label>{sentenceCase(status)}</label> */}
        <span style={{ fontSize: '10px' }}>{lastEvent?.created}</span>
        {isConsumer && repairCase.unreadCountConsumer > 0 && (
          <NotificationCount>
            {repairCase.unreadCountConsumer}
          </NotificationCount>
        )}
        {!isConsumer && repairCase.unreadCountRepairer > 0 && (
          <NotificationCount>
            {repairCase.unreadCountRepairer}
          </NotificationCount>
        )}
      </ChatTimeStatus>
    </StyledChatItemBox>
  );
}

type Props = {
  userRole: UserRole;
};
export default function ChatList({ userRole }: Props): JSX.Element {
  const params = useQueryParams();
  const urlparmas = useUrlParams(['id']);
  const [searchTerm, setSearchTerm] = useState('');
  const [newDot, setNewDot] = useState(false);
  const [inProgressDot, setInProgressDot] = useState(false);
  const [completedDot, setCompletedDot] = useState(false);
  const [chatsFiltered, setChatsFiltered] = useState<RepairCase[]>();
  const {
    resource: chatList,
    loading,
    loadingMissing,
    request,
  } = useGetChatList();
  const loadingRef = useWatchAsRef(loading);
  const [activeTab, setActiveTab] = useState(
    urlparmas?.id !== 'all' ? urlparmas?.id : 'initiated'
  );
  const pollInterval = usePollInterval('infrequent');
  useEffect(() => {
    if (pollInterval) {
      const interval = setInterval(() => {
        if (!loadingRef.current) request();
      }, pollInterval);
      return () => clearInterval(interval);
    }
  }, [pollInterval, loadingRef, request]);

  const [filterKey, setFilterKey] = useState<ChatFilterKey>(
    urlparmas.id !== 'all'
      ? (urlparmas?.id as ChatFilterKey)
      : 'initiated'
  );

  useEffect(() => {
    const currentFilter =
      chatFilters.find(f => f.key === filterKey)?.filter ||
      (() => true);

    let chatsFilteredTemp = chatList
      ?.filter(currentFilter)
      .sort((c1, c2) => {
        const v1 =
          new Date(c1.lastEvent?.created || '').valueOf() || 0;
        const v2 =
          new Date(c2.lastEvent?.created || '').valueOf() || 0;
        return v2 - v1; // descending = largest date first = newest first
      });
    if (searchTerm) {
      if (appEnv.APP_VARIANT === AppVariant.BUSINESS) {
        chatsFilteredTemp = chatsFilteredTemp?.filter(
          chatItem =>
            chatItem?.userDetails?.firstName
              .toLowerCase()
              .indexOf(searchTerm.toLowerCase()) >= 0 ||
            (chatItem?.userDetails?.lastName &&
              chatItem?.userDetails?.lastName
                .toLowerCase()
                .indexOf(searchTerm.toLowerCase()) >= 0)
        );
      } else {
        chatsFilteredTemp = chatsFilteredTemp?.filter(
          conChatItem =>
            conChatItem?.repairerDetails?.displayName &&
            conChatItem?.repairerDetails?.displayName
              .toLowerCase()
              .indexOf(searchTerm.toLowerCase()) >= 0
        );
      }
    }
    setChatsFiltered(chatsFilteredTemp);
    if (chatList && chatList?.length) {
      setNewDot(false);
      setInProgressDot(false);
      setCompletedDot(false);
      if (chatList[0].chatListNewCount) setNewDot(true);

      if (chatList[0].chatListInProgressCount) setInProgressDot(true);

      if (chatList[0].chatListCompleteCount) setCompletedDot(true);
    }
    // if (activeTab === 'new') {
    //   setNewDot(false);
    // } else if (activeTab === 'inProgress') {
    //   setInProgressDot(false);
    // } else if (activeTab === 'completed') {
    //   setCompletedDot(false);
    // }
    // activeTab
  }, [chatList, filterKey, searchTerm]);

  // const chatsFiltered = chatList
  //   ?.filter(currentFilter)
  //   .sort((c1, c2) => {
  //     const v1 = new Date(c1.lastEvent.created || '').valueOf() || 0;
  //     const v2 = new Date(c2.lastEvent.created || '').valueOf() || 0;
  //     return v2 - v1; // descending = largest date first = newest first
  //   });

  useEffectOnce(() => {
    const urlQueryFilter = params.get(UrlQueryKey.FILTER_CATEGORY);
    const foundFilter = chatFilters
      .map(f => f.key)
      .find(k => k === urlQueryFilter);
    if (foundFilter) setFilterKey(foundFilter);
  });
  const TasksTabs = styled(Tabs)`
    ${styledMixins.softShadow}
    .MuiBadge-dot {
      right: -10px;
    }
  `;
  const foundChats = (
    <>
      <div
        style={{
          position: 'sticky',
          // width: '88%',
          top: '0',
          backgroundColor: '#ffffff',
          zIndex: 2,
        }}
      >
        <TasksTabs
          value={activeTab}
          onChange={(e, val) => {
            setActiveTab(val as string);
            setFilterKey(val as ChatFilterKey);
          }}
          variant="fullWidth"
          textColor="primary"
          indicatorColor="primary"
        >
          {chatFilters.map(({ key }) => (
            <Tab
              value={key}
              key={key}
              style={{ textTransform: 'none' }}
              label={
                (key === 'initiated' && newDot) ||
                (key === 'inProgress' && inProgressDot) ||
                (key === 'completed' && completedDot) ? (
                  <Badge variant="dot" color="primary">
                    {_.startCase(key)}
                  </Badge>
                ) : (
                  _.startCase(key)
                )
              }
            />
          ))}
        </TasksTabs>
        <Form>
          <div className="flex items-right">
            <TextInput
              placeholder="Search"
              type="text"
              minHeight={40}
              InputProps={{
                inputMode: 'search',
                onChange: evt => {
                  setSearchTerm(evt.target.value);
                },
                value: searchTerm,
                endAdornment: (
                  <IconButton
                    type="button"
                    disabled={loading}
                    className="text-primary"
                  >
                    <Icon icon={IconLib.SEARCH} size={20} />
                  </IconButton>
                ),
              }}
              fullWidth
            />
          </div>
        </Form>
      </div>
      {chatsFiltered?.length ? (
        chatsFiltered.map(repairCase => (
          <ChatListItem {...{ repairCase }} key={repairCase.id} />
        ))
      ) : (
        <Body
          centered
          css={css`
            flex-grow: 1;
            ${styledMixins.flexCenter}
            padding: ${styledFuncs.pad(2)};
          `}
        >
          No {_.lowerCase(filterKey)} repairs found. Try a different
          tab to see more.
        </Body>
      )}
    </>
  );

  const noChats = (
    <CenteredBox>
      <Body>
        {userRole === UserRole.CONSUMER
          ? "Nothing to see here yet - this is where you'll find chats with repairers when you've reported a fault"
          : "No outstanding repairs - sit tight and we'll send customers your way shortly"}
      </Body>
    </CenteredBox>
  );

  return (
    <>
      <LoadingOverlay loadingComplete={!loadingMissing} />
      {chatList?.length ? foundChats : noChats}
    </>
  );
}
